import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, User } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const ContactForm = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  
  // Success popup state
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Load user data from local storage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        // Check if the data has a 'value' property (as shown in the user's data structure)
        const userInfo = parsedUserData.value || parsedUserData;
        
        // Format the name from firstName and lastName
        const fullName = userInfo.firstName && userInfo.lastName 
          ? `${userInfo.firstName} ${userInfo.lastName}` 
          : '';
        
        // Format the phone number - remove the first two digits (country code) if they exist
        let phoneNumber = userInfo.phone || '';
        if (phoneNumber && phoneNumber.length > 2) {
          // Remove the first two digits (country code) if they exist
          phoneNumber = phoneNumber.substring(2);
        }
        
        setFormData(prevState => ({
          ...prevState,
          name: fullName,
          email: userInfo.email || '',
          phone: phoneNumber
        }));
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }
  }, []);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Handle phone input change (special case for the phone input component)
  const handlePhoneChange = (value) => {
    setFormData(prevState => ({
      ...prevState,
      phone: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Here you would typically use an API endpoint to send an email
      // This is a placeholder for the actual email sending logic
      console.log("Sending email to balajiramesh0402@gmail.com with:", formData);
      
      // In a real implementation, you would have:
      const response = await fetch('https://atabackend.quindl.com/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: 'New Message from Contact Form',
          message: formData.message, 
          senderEmail: formData.email, 
          name: formData.name, 
          phoneNumber: formData.phone
        })
      });
      
      // Show success message
      setShowSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: ''
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error("Failed to send email:", error);
      alert("Failed to send message. Please try again later.");
    }
  };
  
  return (
    <div className="w-full min-h-screen bg-white flex items-center justify-center">
      <div className="max-w-7xl mx-auto p-8 w-full mt-12">
        {/* Form Section */}
        <div className="w-full">
          <div className="mb-12 text-center">
            <h1 className="text-5xl font-bold text-gray-900">Get in touch with us</h1>
            <p className="text-gray-600 mt-2">Fill in this form and we will get back to you shortly</p>
          </div>
          
          <form className="space-y-8 max-w-4xl mx-auto" onSubmit={handleSubmit}>
            {/* Name Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <User className="w-5 h-5 text-orange-500" />
                <span className="font-medium">Name<span className="text-red-500">*</span></span>
              </label>
              <input
                type="text"
                name="name"
                placeholder="Enter your Name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full p-4 rounded-md bg-orange-50/50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Phone Field with react-phone-input-2 */}
            <div className="space-y-2">
              <label className="flex items-center gap-8">
                <span className="font-medium">Phone<span className="text-red-500">*</span></span>
              </label>
              <PhoneInput
                country={'au'}
                value={formData.phone}
                onChange={handlePhoneChange}
                inputProps={{
                  name: 'phone',
                  required: true,
                  className: "w-full px-16 rounded-md"
                }}
                containerClass="phone-input"
                buttonStyle={{ background: 'white', border: '1px solid #e5e7eb' }}
                containerStyle={{ width: '100%' }}
                inputStyle={{ 
                  width: '100%', 
                  height: '56px',
                  fontSize: '16px',
                  background: 'rgba(249, 115, 22, 0.05)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem'
                }}
              />
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-500" />
                <span className="font-medium">Email<span className="text-red-500">*</span></span>
              </label>
              <input
                type="email"
                name="email"
                placeholder="e.g., email@example.com"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full p-4 rounded-md bg-orange-50/50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Message Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-orange-500" />
                <span className="font-medium">Your Message<span className="text-red-500">*</span></span>
              </label>
              <textarea
                name="message"
                placeholder="Enter text here"
                required
                rows={4}
                value={formData.message}
                onChange={handleChange}
                className="w-full p-4 rounded-md bg-orange-50/50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                className="px-12 py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
              >
                Send
              </button>
            </div>
          </form>
          
          {/* Success Popup */}
          {showSuccess && (
            <div className="fixed top-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md z-50 animate-fade-in">
              <div className="flex">
                <div className="py-1">
                  <svg className="w-6 h-6 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <p className="font-bold">Message Sent!</p>
                  <p className="text-sm">We'll get back to you as soon as possible.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Add some basic CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        .phone-input .selected-flag {
          border-radius: 0.375rem 0 0 0.375rem;
        }
      `}</style>
    </div>
  );
};

export default ContactForm;